import { makeUniqueBinary } from '@/helpers/uniqueFilename'
import { useDocumentContainerStore } from '@/stores/modules/documentContainer.ts'
import { useDocumentStore } from '@/stores/modules/document.ts'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { useCraftsmanTaskStore } from '@/stores/modules/craftsmanTask.ts'
import { useTaskStore } from '@/stores/modules/task.ts'
import { useCisStore } from '@/stores/modules/cis.ts'
import { cloneDeep } from 'lodash'

dayjs.locale('de')

const storeMap = {
	craftsman_tasks: useCraftsmanTaskStore,
	tasks: useTaskStore,
	cis: useCisStore,
}

const Upload = {
	mapAttachment({ id }, attachments = {}) {
		const list = []
		for (const endpoint in attachments) {
			if (!attachments[endpoint]) continue
			if (!Array.isArray(attachments[endpoint])) {
				attachments[endpoint] = [{ id: attachments[endpoint].id }]
			}
			attachments[endpoint].forEach((element) => {
				list.push({
					id,
					target: endpoint,
					targetId: element.id,
				})
			})
		}
		return list
	},
	mapInitContainerData(containers = []) {
		return containers.map((container) => {
			const { id, comment, hp_id, document_files } = container
			const files = document_files.map((file) => ({
				id: file.id,
				name: file.file_notes,
				type: file.document_type,
			}))
			return {
				id,
				hp_id,
				comment,
				files,
				uploaded: true,
				action: false,
			}
		})
	},
	mapFileData(container, file) {
		const data = new FormData()

		data.append('container_id', container.id)
		data.append('file_notes', file.name)
		data.append('file_type', file.type.id)
		data.append('ts', dayjs().utc(true).unix().toString())
		data.append('is_web_upload', JSON.stringify(true))
		data.append('binary', makeUniqueBinary(file.file))

		return data
	},
	sortByActionMixin(containers = []) {
		const create = [],
			detach = [],
			remove = [],
			update = []

		containers.forEach((item) => {
			if (!item.uploaded) {
				create.push(item)
			} else if (item.uploaded && item.action === 'detach') {
				detach.push(item)
			} else if (item.uploaded && item.action === 'delete') {
				remove.push(item)
			} else {
				update.push(item)
			}
		})

		return { create, detach, remove, update }
	},
	async postContainerWithFiles(data) {
		const documentContainerStore = useDocumentContainerStore()
		const documentStore = useDocumentStore()
		const { create } = this.sortByActionMixin(data)
		return await Promise.all(
			create.map((container) =>
				documentContainerStore
					.create({
						data: { comment: container.comment },
					})
					.then((response) =>
						Promise.all(
							container.files.map((file) =>
								documentStore.create({
									data: this.mapFileData(response, file),
									headers: {
										'Content-Type': 'multipart/form-data',
									},
									onUploadProgress: (ev) => {
										file.progress = Math.round((ev.loaded * 100) / ev.total)
									},
								})
							)
						).then(() => {
							return { ...response, ...{ posted: true } }
						})
					)
			)
		)
	},
	async postAndAttachDocuments({ links, containers }) {
		if (containers.some((container) => container.files.length <= 0)) {
			return Promise.resolve()
		}
		const documents = await this.postContainerWithFiles(
			cloneDeep(containers).filter((container) => container.files.length > 0)
		)
		await Promise.all(
			documents.map(async (container) => {
				await this.$func.updateLinks({
					module: 'documentcontainer',
					moduleId: container.id,
					links,
				})
			})
		).then((data) => {
			return data
		})
	},
	async updateAndAttachDocuments({ id, module, links, initLinks, containers }) {
		const documentContainerStore = useDocumentContainerStore()
		if (containers.some((container) => container.files.length <= 0)) {
			return Promise.resolve()
		}
		const data = structuredClone(containers).filter(
			(container) => container.files.length > 0
		)
		const { detach, remove, update } = this.sortByActionMixin(data)
		const documents = [...(await this.postContainerWithFiles(data)), ...update]
		if (Array.isArray(documents) && documents.length > 0) {
			documents.map(async (container) => {
				await this.$func.updateLinks({
					module: 'documentcontainer',
					moduleId: container.id,
					links,
					initLinks: container.posted ? [] : initLinks,
				})
			})
		}

		await Promise.all(
			this.mapAttachment(
				{ id },
				{
					documents: detach,
				}
			).map((attachment) =>
				storeMap[module] ? storeMap[module]().detach(attachment) : undefined
			)
		)

		await Promise.all(
			remove.map((container) => documentContainerStore.remove(container))
		)
	},
}

export default Upload
